import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  salariesService,
  type CreateSalaryData,
  type UpdateSalaryData,
} from '@/services/salaries.service';

// Query key factory
export const salaryKeys = {
  all: ['salaries'] as const,
  user: (userId: string) => [...salaryKeys.all, 'user', userId] as const,
};

// Get user salaries
export function useUserSalaries(userId?: string) {
  return useQuery({
    queryKey: salaryKeys.user(userId!),
    queryFn: () => salariesService.getUserSalaries(userId!),
    enabled: !!userId,
  });
}

// Create salary
export function useCreateSalary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSalaryData) => salariesService.createSalary(data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.user(variables.userId) });
      // Also invalidate employee profile data since salary is part of it
      void queryClient.invalidateQueries({ queryKey: ['employees', 'profile', variables.userId] });
    },
  });
}

// Update salary
export function useUpdateSalary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; userId: string; data: UpdateSalaryData }) =>
      salariesService.updateSalary(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.user(variables.userId) });
      // Also invalidate employee profile data since salary is part of it
      void queryClient.invalidateQueries({ queryKey: ['employees', 'profile', variables.userId] });
    },
  });
}

// Delete salary
export function useDeleteSalary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; userId: string }) => salariesService.deleteSalary(id),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.user(variables.userId) });
      // Also invalidate employee profile data since salary is part of it
      void queryClient.invalidateQueries({ queryKey: ['employees', 'profile', variables.userId] });
    },
  });
}
